import React, { useState, useEffect, useRef } from 'react';
import './SecretsModal.css';
import { modalInputClass } from './modalInputClass';
import { useModalSelectionGuard, preventModalOverlaySelection } from '../hooks/useModalSelectionGuard';

const AutocloseModal = ({
  isOpen,
  onClose,
  onSubmit,
  broker,
  defaultCount,
  defaultAnnual,
  defaultSpread
}) => {
  useModalSelectionGuard(isOpen);

  const [count, setCount] = useState('');
  const [annual, setAnnual] = useState('');
  const [spread, setSpread] = useState('');
  const [error, setError] = useState(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (!wasOpenRef.current) {
        const rawCount =
          defaultCount != null && defaultCount !== '' ? String(defaultCount).replace(/[^\d]/g, '') : '';
        setCount(rawCount);
        setAnnual(
          defaultAnnual != null && defaultAnnual !== undefined && !isNaN(defaultAnnual)
            ? Number(defaultAnnual).toFixed(2)
            : ''
        );
        setSpread(
          defaultSpread != null && defaultSpread !== undefined && !isNaN(defaultSpread)
            ? Number(defaultSpread).toFixed(2)
            : ''
        );
      }
      wasOpenRef.current = true;
      setError(null);
    } else {
      wasOpenRef.current = false;
    }
  }, [isOpen, defaultCount, defaultAnnual, defaultSpread]);

  const formatCountWithSpaces = (val) => {
    const digits = String(val || '').replace(/[^\d]/g, '');
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const handleCountChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setCount(value);
    if (error) setError(null);
  };

  const limitToTwoDecimals = (value) => {
    const normalized = value.replace(',', '.');
    if (normalized === '' || normalized === '.' || normalized === '-') return true;
    const parts = normalized.split('.');
    return parts.length <= 1 || parts[1].length <= 2;
  };

  const handleAnnualChange = (e) => {
    const value = e.target.value;
    if (value.indexOf('-') > 0) return;
    const body = value.startsWith('-') ? value.slice(1) : value;
    if (!/^[\d.,]*$/.test(body)) return;
    if (!limitToTwoDecimals(value.replace(',', '.'))) return;
    setAnnual(value);
    if (error) setError(null);
  };

  const handleSpreadChange = (e) => {
    const value = e.target.value;
    if (value.indexOf('-') > 0) return;
    const body = value.startsWith('-') ? value.slice(1) : value;
    if (!/^[\d.,]*$/.test(body)) return;
    if (!limitToTwoDecimals(value.replace(',', '.'))) return;
    setSpread(value);
    if (error) setError(null);
  };

  const handleCancel = () => {
    setCount('');
    setAnnual('');
    setSpread('');
    setError(null);
    onClose();
  };

  const defaultCountValue =
    defaultCount != null && defaultCount !== '' ? String(defaultCount).replace(/[^\d]/g, '') : '';
  const defaultAnnualValue =
    defaultAnnual != null && defaultAnnual !== undefined && !isNaN(defaultAnnual)
      ? Number(defaultAnnual).toFixed(2)
      : '';
  const defaultSpreadValue =
    defaultSpread != null && defaultSpread !== undefined && !isNaN(defaultSpread)
      ? Number(defaultSpread).toFixed(2)
      : '';

  const handleOk = () => {
    const countValue = parseInt(count.replace(/\s/g, ''), 10) || 0;
    const annualTrim = annual.trim();
    const annualValue = annualTrim ? parseFloat(annual.replace(',', '.')) : null;
    const spreadTrim = spread.trim();
    const spreadValue = spreadTrim ? parseFloat(spread.replace(',', '.')) : null;

    if (countValue <= 0) {
      setError('Количество должно быть больше 0');
      return;
    }

    if (annualTrim && (annualTrim === '-' || isNaN(annualValue))) {
      setError('Годовых должно быть числом');
      return;
    }

    if (spreadTrim && (spreadTrim === '-' || isNaN(spreadValue))) {
      setError('Спред должен быть числом');
      return;
    }

    const round2 = (v) => (v != null ? Math.round(v * 100) / 100 : null);
    onSubmit({
      sum: countValue,
      annualPercent: round2(annualValue),
      spread: round2(spreadValue)
    });
  };

  if (!isOpen) return null;

  const brokerLabel = broker === 'bcs' ? 'БКС' : broker === 'finam' ? 'Финам' : broker;

  return (
    <div className="secrets-modal-overlay" onSelectStart={preventModalOverlaySelection}>
      <div className="secrets-modal" onClick={e => e.stopPropagation()}>
        <div className="secrets-modal-header">
          <h2>Автозакрытие</h2>
          <button type="button" className="secrets-close-btn" onClick={handleCancel} aria-label="Закрыть">×</button>
        </div>

        <div className="secrets-form">
          <div className="form-group">
            <label>Брокер</label>
            <input
              type="text"
              value={brokerLabel}
              readOnly
              className={modalInputClass({ readonly: true })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="autocloseCount">Количество</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="autocloseCount"
                value={formatCountWithSpaces(count)}
                onChange={handleCountChange}
                placeholder="Целое число больше 0"
                className={modalInputClass({ withActions: true, error: Boolean(error) })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setCount(defaultCountValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить количество по умолчанию"
                  disabled={count === defaultCountValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCount('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить количество"
                  disabled={!count}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="autocloseSpread">Спред % (и ниже)</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="autocloseSpread"
                value={spread}
                onChange={handleSpreadChange}
                placeholder="%"
                className={modalInputClass({ withActions: true, error: Boolean(error) })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setSpread(defaultSpreadValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить спред по умолчанию"
                  disabled={spread === defaultSpreadValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSpread('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить спред"
                  disabled={!spread}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="autocloseAnnual">% годовых (и выше)</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="autocloseAnnual"
                value={annual}
                onChange={handleAnnualChange}
                placeholder="%"
                className={modalInputClass({ withActions: true, error: Boolean(error) })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setAnnual(defaultAnnualValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить годовой процент по умолчанию"
                  disabled={annual === defaultAnnualValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAnnual('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить годовой процент"
                  disabled={!annual}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="secrets-modal-buttons">
            <button type="button" className="secrets-cancel-btn" onClick={handleCancel}>
              Отмена
            </button>
            <button type="button" className="secrets-save-btn" onClick={handleOk}>
              Создать
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutocloseModal;
